import type { ChartConfig } from '@/components/atomic/segment-chart/base'

import { calculatePercentage } from '@/utils/calculate-statistics'

import { groupIntoMaturityBuckets } from './maturity-bucket-helpers'

export function getChartConfig<T>(
  data: Record<string, number | undefined>,
  labelFormatter?: (value: T) => string,
): ChartConfig {
  const object: ChartConfig = {}
  if (!data) return object

  // TODO: Add to tailwind config
  const colors = [
    'var(--color-distribution-moment-chart-forest-full)',
    'var(--color-distribution-moment-chart-purple-full)',
    'var(--color-distribution-moment-chart-orange-full)',
    'var(--color-distribution-moment-chart-blue-full)',
    'var(--color-distribution-moment-chart-yellow-full)',
    'var(--color-distribution-moment-chart-pink-full)',
    'var(--color-distribution-moment-chart-green-full)',
    'var(--color-distribution-moment-chart-red-full)',
    'var(--color-distribution-moment-chart-forest-half)',
    'var(--color-distribution-moment-chart-purple-half)',
    'var(--color-distribution-moment-chart-orange-half)',
    'var(--color-distribution-moment-chart-blue-half)',
    'var(--color-distribution-moment-chart-yellow-half)',
    'var(--color-distribution-moment-chart-pink-half)',
    'var(--color-distribution-moment-chart-green-half)',
    'var(--color-distribution-moment-chart-red-half)',
  ]

  // Filter undefined values and sort entries by value in descending order
  const sortedEntries = Object.entries(data)
    .filter((entry): entry is [string, number] => entry[1] !== undefined)
    .sort(([, a], [, b]) => b - a)

  sortedEntries.forEach(([key, _value], index) => {
    let label = key
    if (key === 'cash') label = 'Cash'
    else if (key === 'null') label = 'N/A'
    else if (labelFormatter) label = labelFormatter(key as T)

    object[key] = {
      label,
      color: colors[index % colors.length],
    }
  })
  return object
}

export function formatCouponLabel(label: string): string {
  const splitResult = label.split('_')
  if (splitResult.length === 2) {
    const left = splitResult[0]
    const right = splitResult[1]
    if (right === 'inf') {
      return `${left}%+`
    }
    return `${left}-${right}%`
  }
  return `${splitResult[0]}%`
}

export function convertDistributionData(
  data: Record<string, number | undefined>,
  total: number,
  useMaturityBuckets: boolean = false,
  isMaturityData: boolean = false,
): Record<string, number> {
  if (!data) return {}

  // If this is maturity data and we should use buckets
  if (isMaturityData && useMaturityBuckets) {
    return groupIntoMaturityBuckets(data, total)
  }

  // Otherwise use the standard conversion
  return Object.fromEntries(
    Object.entries(data)
      .filter((entry): entry is [string, number] => entry[1] !== undefined)
      .map(([key, value]) => [key, calculatePercentage(value, total, false)]),
  )
}
